import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useBoolean } from '@sravni/react-utils';
import { Controller, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Space, Typography } from '@sravni/react-design-system';
import { FORM_FIELDS, LABELS, PAGE, PAGE_CONTEXT } from '../../../../constants';
import { EventType } from '../../../../helpers/analytics';
import { amountTransform, Col, Rows, SelectInput, TextInput } from '../../../landing.common';
import { Row } from '../common';
import { useSelection } from '../../../../store/reducers/selection';
import { useDictionary } from '../../../../store/reducers/dictionaries';
import { useLoggedIn } from '../../../../store/reducers/user';
import { StepButtons } from '../../ui/step/components/step.buttons';
import commonStyles from '../common.module.scss';
import { useAnalytics } from '../../../hooks/analytics';
import { PURPOSE_SOURCE_VALUES } from '../../../../constants/sources';
import { useFormData, useFormStore } from '../../../../store/selectors/formStore';
import { Layout } from '../../ui/step';
import { StepContext } from '../../creator/step.context';
import { scrollToForm } from '../../../../helpers';
import { useAbAdditionalQuestions } from '../../../../helpers/experiments/additional.questions';
import { useFormOnBlur } from '../../hooks/formHooks';
import { RegionField } from '../../../form.common/fields';
import { extendFormData, setPageContext } from '../../../../store/reducers/formStore';
import { useEsiaReturnAnalytic } from '../../hooks/esiaReturnAnalytic';
import { schema } from './schema';
import { AutoCreditFields, ContactDetailsFields } from './components';
import { AuthSms } from './auth';
import { WhenMoneyNeeded } from './components/when.money.needed';

export const ParamsStepView: FC = React.memo(() => {
    const [loginView, setLoginView] = useBoolean(false);
    const { page } = useSelection();
    const dispatch = useDispatch();
    const purposes = useDictionary('purposes');
    const loggedIn = useLoggedIn();
    const { disabledFields = [], userId, headline } = useFormStore();
    const formData = useFormData();
    const { pushEvent } = useAnalytics();
    const { nextStep, analyticStepText } = useContext(StepContext);

    const form = useForm({
        resolver: yupResolver(schema()),
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const {
        control,
        watch,
        formState: { errors, dirtyFields: _, isSubmitting, isValidating },
        clearErrors,
        reset,
        setValue,
        handleSubmit,
        getValues,
    } = form;

    const additionalQuestionsVariant = useAbAdditionalQuestions();
    const isShowAdditionalQuestions = additionalQuestionsVariant === '1';

    useEsiaReturnAnalytic('Шаг 1|Параметры');

    useEffect(() => {
        reset({ ...formData }, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    useEffect(() => {
        setFullName();
        scrollToForm();
        dispatch(setPageContext(PAGE_CONTEXT.STEP_1));
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Просмотр экрана|Шаг 1|Параметры',
        });
    }, []);

    const purposeOptions = useMemo(() => purposes.map(({ name, value }) => ({ value, label: name })), [purposes]);

    const onContinueButtonClick = useCallback(() => {
        setValue(FORM_FIELDS.AGREEMENT, true);
    }, []);

    // todo применять выше при загрузке данных
    const setFullName = useCallback(() => {
        const fullName = [
            formData[FORM_FIELDS.LAST_NAME],
            formData[FORM_FIELDS.FIRST_NAME],
            formData[FORM_FIELDS.MIDDLE_NAME],
        ]
            .filter((v) => v)
            .join(' ');
        setValue(FORM_FIELDS.FULL_NAME, fullName);
    }, [formData, setValue]);

    const goNext: SubmitHandler<FE.FormData> = useCallback(
        async (validFields) => {
            dispatch(extendFormData(validFields));

            if (userId || [PAGE.CALLCENTER, PAGE.AGENTS].includes(page)) {
                nextStep();
                return;
            }

            setLoginView.on();
            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Получить код по СМС',
            });
        },
        [page, setLoginView, userId, pushEvent, nextStep],
    );

    const onSubmitError: SubmitErrorHandler<Record<string, unknown>> = useCallback(
        (e) => {
            const errorKey = Object.keys(e)[0];
            const errorValue = getValues(errorKey);

            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Невалидная анкета',
                eventLabel: `${analyticStepText}|${errorKey}|Ручной проход|${errorValue}`,
            });
        },
        [analyticStepText],
    );

    const [selectedPurpose] = watch([FORM_FIELDS.PURPOSE]);

    const formOnBlur = useFormOnBlur(form);

    const formOnChange: React.FormEventHandler<HTMLFormElement> = (event) => {
        const { name: fieldName } = event.target as HTMLInputElement;
        if (fieldName && fieldName in errors) {
            clearErrors(fieldName);
        }
    };

    const isAutoCreditSelected = useMemo(
        () => [PURPOSE_SOURCE_VALUES.NEW_CAR, PURPOSE_SOURCE_VALUES.USED_CAR].includes(selectedPurpose),
        [selectedPurpose],
    );

    const isShowAuth = page === PAGE.AVITO ? !loggedIn : loginView;

    const withoutGreenMessage = useMemo(
        () => loggedIn || [PAGE.AVITO, PAGE.WHITELABEL, PAGE.AGENTS].includes(page),
        [loggedIn, page],
    );

    if (isShowAuth) {
        return <AuthSms form={form} onBlur={formOnBlur} onChange={formOnChange} />;
    }

    return (
        <Layout
            onBlur={formOnBlur}
            onSubmit={handleSubmit(goNext, onSubmitError)}
            onChange={formOnChange}
            headline={headline}
            locked={isSubmitting}
            step={1}
            greenMessageIcon="heart"
            greenMessage={
                withoutGreenMessage
                    ? undefined
                    : 'Мы создадим вам личный кабинет, в котором вы сможете отслеживать изменение статуса ваших заявок на получение кредита.'
            }>
            <Rows>
                <Row>
                    <Col md-4>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.PURPOSE}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label={LABELS[FORM_FIELDS.PURPOSE]}
                                    disabled={disabledFields.includes(FORM_FIELDS.PURPOSE)}
                                    options={purposeOptions}
                                    errorMessage={errors[FORM_FIELDS.PURPOSE]?.message}
                                    withCrossIcon={false}
                                />
                            )}
                        />
                    </Col>
                    <Col md-4>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.AMOUNT}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label={LABELS[FORM_FIELDS.AMOUNT]}
                                    value={amountTransform.input(field)}
                                    onChange={amountTransform.output(field)}
                                    errorMessage={errors[FORM_FIELDS.AMOUNT]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-4>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.REGION}
                            render={({ field }) => <RegionField field={field} form={form} />}
                        />
                    </Col>
                </Row>
                {isShowAdditionalQuestions && <WhenMoneyNeeded form={form} />}
                {isAutoCreditSelected && <AutoCreditFields form={form} />}
                {/** Баннер ЦИАН удален в рамках CF-1913 */}
                <ContactDetailsFields form={form} />
            </Rows>
            <StepButtons
                onContinueButtonClick={onContinueButtonClick}
                continueButtonDisabled={isValidating}
                className={commonStyles['step-buttons-wrapper']}
            />

            {page === PAGE.AVITO ? (
                <Space justify={'center'}>
                    <Typography.Text className={'h-color-D60 h-mt-40 h-text-center'}>
                        Услуги по подбору кредитных и финансовых продуктов оказываются ООО &quot;Сравни.Ру&quot;.
                    </Typography.Text>
                </Space>
            ) : null}
        </Layout>
    );
});

ParamsStepView.displayName = 'ParamsStepView';
